import moment from "moment-jalaali"
import { formatJalaliParts } from "@/app/utils/jalaliDate"

/** Which parts of the date the user has explicitly picked in this session. */
export type JalaliPanelParts = { year: boolean; month: boolean; day: boolean }

export type JalaliPanelDate = { year: number; month: number; day: number }

/** The year/month the grid is currently showing, which is not the selection. */
export type JalaliPanelView = { year: number; month: number }

export type JalaliPanelState = {
	view: JalaliPanelView
	selected: JalaliPanelDate | null
	picked: JalaliPanelParts
}

/** What to report upwards, or null when the value must not change. */
export type JalaliPanelCommit = { value: string; isComplete: boolean } | null

export type JalaliPanelTransition = {
	state: JalaliPanelState
	commit: JalaliPanelCommit
}

const NO_PARTS_PICKED: JalaliPanelParts = { year: false, month: false, day: false }

const clampToMonth = (view: JalaliPanelView, day: number) =>
	Math.min(day, moment.jDaysInMonth(view.year, view.month))

export const isSameJalaliDate = (
	left: JalaliPanelDate | null,
	right: JalaliPanelDate | null,
): boolean => {
	if (!left || !right) return left === right
	return left.year === right.year && left.month === right.month && left.day === right.day
}

export const createPanelState = (
	selected: JalaliPanelDate | null,
	fallbackView: JalaliPanelView,
): JalaliPanelState => ({
	view: selected ? { year: selected.year, month: selected.month } : fallbackView,
	selected,
	picked: NO_PARTS_PICKED,
})

const commitFor = (state: JalaliPanelState): JalaliPanelCommit => {
	// Only an explicit day selection commits. Browsing months or years must never
	// rewrite a date the user has not re-picked, so a panel opened on an existing
	// value stays read-only until a day is clicked.
	if (!state.picked.day || !state.selected) return null

	return {
		value: formatJalaliParts(state.selected.year, state.selected.month, state.selected.day),
		isComplete: state.picked.year && state.picked.month && state.picked.day,
	}
}

const moveView = (
	state: JalaliPanelState,
	view: JalaliPanelView,
	picked: JalaliPanelParts,
): JalaliPanelTransition => {
	// The selection follows the view only once the user has chosen a day here;
	// otherwise it stays put, so the highlight does not leak into other months.
	const selected =
		picked.day && state.selected
			? { year: view.year, month: view.month, day: clampToMonth(view, state.selected.day) }
			: state.selected

	const next: JalaliPanelState = { view, selected, picked }
	return { state: next, commit: commitFor(next) }
}

export const selectDay = (state: JalaliPanelState, day: number): JalaliPanelTransition => {
	const next: JalaliPanelState = {
		view: state.view,
		selected: { year: state.view.year, month: state.view.month, day },
		picked: { ...state.picked, day: true },
	}

	return { state: next, commit: commitFor(next) }
}

export const changeMonth = (state: JalaliPanelState, month: number): JalaliPanelTransition =>
	moveView(state, { year: state.view.year, month }, { ...state.picked, month: true })

export const changeYear = (state: JalaliPanelState, year: number): JalaliPanelTransition =>
	moveView(state, { year, month: state.view.month }, { ...state.picked, year: true })

export const shiftMonth = (state: JalaliPanelState, offset: number): JalaliPanelTransition => {
	// Plain month arithmetic: no moment instance to overflow when the current day
	// does not exist in the target month.
	const absoluteMonth = state.view.year * 12 + state.view.month + offset

	return moveView(
		state,
		{ year: Math.floor(absoluteMonth / 12), month: ((absoluteMonth % 12) + 12) % 12 },
		{ ...state.picked, month: true },
	)
}

/** Re-syncs to a value changed outside the panel, e.g. typed into the input. */
export const syncToValue = (
	state: JalaliPanelState,
	selected: JalaliPanelDate | null,
): JalaliPanelState => {
	if (isSameJalaliDate(state.selected, selected)) return state

	return {
		...state,
		selected,
		view: selected ? { year: selected.year, month: selected.month } : state.view,
	}
}
